import {useEffect, useState} from 'react';
import {Button, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import classnames from 'classnames';
import styles from './news.module.scss';

export const NO_PERMISSION =
  "You don't have permission to change this. Ask an admin.";

// A label with its ⓘ tooltip.
export const Tip = ({label, tip}) => (
  <span className={styles.tipLabel}>
    {label}
    {tip ? (
      <Tooltip title={tip}>
        <InfoCircleOutlined className={styles.tipIcon} />
      </Tooltip>
    ) : null}
  </span>
);

// Every action button carries a tooltip. Without permission it is disabled
// and the tooltip says why. The span lets a disabled button show a tooltip.
//
// `allowed` is checked whenever it is passed: a role with no grant for the
// tab has no operations at all, so the value arrives as undefined and must
// read as "not allowed". Only a button that passes no `allowed` is ungated.
export const ActionButton = ({tip, disabled, ...props}) => {
  const {allowed: grant, ...buttonProps} = props;
  const allowed = 'allowed' in props ? Boolean(grant) : true;
  return (
    <Tooltip title={allowed ? tip : NO_PERMISSION}>
      <span className={styles.buttonWrap}>
        <Button disabled={!allowed || disabled} {...buttonProps} />
      </span>
    </Tooltip>
  );
};

// Brand-coloured single choice, in place of antd's blue radio buttons.
export const ChoiceGroup = ({value, onChange, options, disabled}) => (
  <div className={styles.choices} role="radiogroup">
    {options.map(option => (
      <Tooltip key={option.value} title={option.tip}>
        <button
          type="button"
          role="radio"
          aria-checked={value === option.value}
          disabled={disabled}
          className={classnames(
            styles.choice,
            value === option.value && styles.choiceOn,
          )}
          onClick={() => onChange?.(option.value)}>
          <strong>{option.label}</strong>
          {option.hint ? <span>{option.hint}</span> : null}
        </button>
      </Tooltip>
    ))}
  </div>
);

// Decided once per page load, so the app remounting a page while it starts
// up doesn't close the box before anyone has read it.
const firstVisit = {};
const isFirstVisit = key => {
  if (!(key in firstVisit)) {
    try {
      firstVisit[key] = localStorage.getItem(key) !== 'seen';
    } catch {
      firstVisit[key] = true;
    }
  }
  return firstVisit[key];
};

// The short "How this works" box at the top of each tab: open the first
// time someone sees it, closed after that.
export const HowItWorks = ({id, children}) => {
  const key = `news-how-it-works-${id}`;
  const [open, setOpen] = useState(() => isFirstVisit(key));

  useEffect(() => {
    try {
      localStorage.setItem(key, 'seen');
    } catch {
      // Without storage it simply opens every time.
    }
  }, [key]);

  return (
    <div className={styles.how}>
      <button
        type="button"
        className={styles.howToggle}
        aria-expanded={open}
        onClick={() => setOpen(!open)}>
        <InfoCircleOutlined /> How this works {open ? '▴' : '▾'}
      </button>
      {open ? <p>{children}</p> : null}
    </div>
  );
};

// A collapsed section for settings editors never need.
export const TechnicalDetails = ({label, children}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.technical}>
      <button
        type="button"
        className={styles.howToggle}
        aria-expanded={open}
        onClick={() => setOpen(!open)}>
        {open ? '▾' : '▸'} {label || 'Technical details (for the tech team)'}
      </button>
      {open ? <div className={styles.technicalBody}>{children}</div> : null}
    </div>
  );
};
